export const DEFAULT_ERROR_MESSAGE = "Algo salió mal";
export const USER_EXISTS_STATUS_CODE = 409;
export const USER_EXISTS_ERROR_CODE = "USER_ALREADY_EXISTS";
function reportToDatadog(error, additionalData = {}) {
    if (typeof window !== "undefined" && window.DD_RUM) {
        window.DD_RUM.addError(error, {
            source: "signup-lib",
            ...additionalData,
        });
    }
}
/**
 * Parses an API error response and returns the appropriate error object
 * based on the status code and error type.
 *
 * @param response - The response object from fetch
 * @returns A specific error type (UserAlreadyExistsError, ApiError, etc.)
 * @throws The specific error type based on the response
 */
export async function handleApiError(response) {
    const status = response.status.toString();
    try {
        const responseData = await response.json();
        const message = responseData.message || DEFAULT_ERROR_MESSAGE;
        const errorCode = responseData.errorCode || responseData.code || null;
        // Handle specific error types based on status and error codes
        if (response.status === USER_EXISTS_STATUS_CODE ||
            errorCode === USER_EXISTS_ERROR_CODE) {
            // Handle user already exists error with potential token
            const token = responseData.token || undefined;
            const authAction = responseData.authAction || undefined;
            return new UserAlreadyExistsError(token, authAction);
        }
        // Default to ApiError for other status codes
        return new ApiError(message, status, errorCode);
    }
    catch {
        // If we can't parse the JSON, return a generic API error
        return new ApiError(DEFAULT_ERROR_MESSAGE, status);
    }
}
/**
 * Base error class for API related errors
 * @property code - The error code returned from the API
 * @property message - Human readable error message
 * @property errorCode - The specific error code from the API (if available)
 */
export class ApiError extends Error {
    code;
    errorCode;
    constructor(message, code, errorCode = null) {
        super(message);
        this.name = "ApiError";
        this.code = code;
        this.errorCode = errorCode;
        reportToDatadog(this);
    }
}
/**
 * Error thrown when input validation fails
 */
export class ValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = "ValidationError";
        reportToDatadog(this);
    }
}
/**
 * Error thrown when a network request fails
 */
export class NetworkError extends Error {
    constructor(message) {
        super(message);
        this.name = "NetworkError";
        reportToDatadog(this);
    }
}
/**
 * Error thrown when attempting to create a user that already exists
 * Contains optional authentication details if the user can directly login
 * @property token - JWT token for authentication if available
 * @property authAction - The auth action to take (e.g. LOGIN, VERIFY, etc)
 */
export class UserAlreadyExistsError extends Error {
    token;
    authAction;
    constructor(token, authAction) {
        super("El usuario ya existe");
        this.name = "UserAlreadyExistsError";
        this.token = token;
        this.authAction = authAction;
        reportToDatadog(this);
    }
}
/**
 * Error thrown when trying to make a network request with incomplete or invalid state
 * Used when required fields are missing or invalid before making an API request
 */
export class InvalidStateError extends Error {
    missingFields;
    constructor(message, missingFields = []) {
        super(message);
        this.name = "InvalidStateError";
        this.missingFields = missingFields;
        reportToDatadog(this, {
            causes: missingFields,
        });
    }
}
